/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useRateLimit } from "@/components/providers/rate-limit-provider";
import { TimerReset } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

export const RateLimitBanner: React.FC = () => {
  const { state } = useRateLimit();
  const [mounted, setMounted] = useState(false);
  // CRITICAL: Initialize to 0 to avoid hydration mismatch
  // Date.now() returns different values on server vs client, causing hydration errors
  // We'll set it to the actual time only after the component mounts on the client
  const [now, setNow] = useState<number>(0);

  useEffect(() => {
    // Set mounted flag and initial time only on client
    setMounted(true);
    setNow(Date.now());

    // Update time every second
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Memoize state values to prevent infinite loops
  const stateRetryAfter = state?.retryAfterSeconds;
  const stateAt = state?.at;
  const stateStatus = state?.status;
  const stateRequestId = state?.requestId;

  const remaining = useMemo(() => {
    // Don't calculate if not mounted or now is not set (still 0)
    if (!mounted || !now || !stateRetryAfter || !stateAt) return null;
    const elapsed = Math.floor((now - stateAt) / 1000);
    const left = Math.max(0, Math.floor(stateRetryAfter - elapsed));
    return left;
  }, [mounted, now, stateRetryAfter, stateAt]);

  if (!mounted || !state || stateStatus !== 429) return null;

  return (
    <div
      suppressHydrationWarning
      role="status"
      aria-live="polite"
      className="w-full border-b bg-destructive/10 border-destructive text-destructive"
    >
      <div className="mx-auto max-w-screen-xl px-4 py-2 flex items-center gap-2 text-sm">
        <TimerReset className="h-4 w-4" />
        You are temporarily rate limited. Please wait
        {typeof remaining === "number" ? ` ~${remaining}s` : ""} before
        retrying.
        {stateRequestId && (
          <span className="ml-2 opacity-75">Request ID: {stateRequestId}</span>
        )}
      </div>
    </div>
  );
};
